import React, { useEffect, useRef, useState } from 'react';
import SearchHeader from '../components/SearchHeader';
import TreeDirectory from '../components/TreeDirectory';
import ContentPanel from '../components/ContentPanel';
import TopNav from '../components/TopNav';
import ContextSearchResults from '../components/ContextSearchResults';
import { accent, darkPurple } from '../utils/colors';
import { fetchRootSkills, fetchChildrenSkills, searchSkills } from '../utils/api';

export default function SkillsTreePage({ apiBase }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openNodes, setOpenNodes] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDropdown, setSearchDropdown] = useState(false);
  const searchTimeout = useRef();
  const searchInputRef = useRef();
  const [expandedDescId, setExpandedDescId] = useState(null);
  const [rootSkills, setRootSkills] = useState([]);

  useEffect(() => {
    if (!apiBase) return;
    setLoading(true);
    fetchRootSkills(apiBase)
      .then(data => {
        let treeData = [];
        if (Array.isArray(data) && data.length === 1 && data[0].level === 0) {
          treeData = data[0].skills;
        } else if (Array.isArray(data)) {
          treeData = data.flatMap(g => g.skills || []);
        }
        setTree(treeData);
        setSelectedNode(null);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [apiBase]);

  useEffect(() => {
    if (!apiBase) return;
    setLoading(true);
    fetchRootSkills(apiBase)
      .then(data => {
        let treeData = [];
        if (Array.isArray(data) && data.length === 1 && data[0].level === 0) {
          treeData = data[0].skills;
        } else if (Array.isArray(data)) {
          treeData = data.flatMap(g => g.skills || []);
        }
        setTree(treeData);
        setSelectedNode(null);
        setLoading(false);

        const roots = treeData.filter(skill => 
          !skill.parentId || skill.parentId === 0 || skill.parentId === null
        );
        setRootSkills(roots);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [apiBase]);

  const loadChildren = async (node) => {
    console.log('=== НАЧАЛО loadChildren ===');
    console.log('Загружаем дочерние элементы для узла:', node);
    
    if (!apiBase || node.childrenLoaded) {
      console.log('API не готов или дочерние элементы уже загружены');
      return [];
    }
    
    try {
      console.log('Вызываем API для загрузки дочерних элементов');
      const data = await fetchChildrenSkills(apiBase, node.id);
      console.log('Полученные данные от API:', data);
      
      const children = Array.isArray(data) && data.length === 1 ? data[0].skills : (Array.isArray(data) ? data.flatMap(g => g.skills || []) : []);
      console.log('Обработанные дочерние элементы:', children);
      
      setTree(prev => addChildrenToNode(prev, node.id, children));
      console.log('Дерево обновлено');
      return children;
    } catch (e) {
      console.error('Ошибка при загрузке дочерних элементов:', e);
      setError(e.message);
      return [];
    }
  };

  function addChildrenToNode(nodes, nodeId, children) {
    return nodes.map(n => {
      if (n.id === nodeId) return { ...n, children, hasChildren: children.length > 0, childrenLoaded: true };
      if (n.children) return { ...n, children: addChildrenToNode(n.children, nodeId, children) };
      return n;
    });
  }

  const handleSelectNode = (node) => setSelectedNode(node);

  // Функция поиска с debounce
  useEffect(() => {
    if (!apiBase || !search.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    
    // Очищаем предыдущий таймаут
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Устанавливаем новый таймаут для debounce
    searchTimeout.current = setTimeout(async () => {
      try {
        console.log('Выполняем поиск:', search.trim());
        const results = await searchSkills(apiBase, search.trim());
        console.log('Результаты поиска:', results);
        setSearchResults(Array.isArray(results) ? results : []);
        setSearchDropdown(true);
      } catch (e) {
        console.error('Ошибка поиска:', e);
        setError(e.message);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, apiBase]);

  // Функция для поиска узла в дереве по ID
  const findAndSelectNodeById = (nodes, targetId) => {
    console.log('Ищем узел с ID:', targetId, 'в дереве:', nodes);
    for (const node of nodes) {
      console.log('Проверяем узел:', node.id, node.title);
      if (node.id === targetId) {
        console.log('Найден узел:', node);
        return node;
      }
      if (node.children) {
        console.log('Ищем в дочерних элементах узла:', node.id);
        const found = findAndSelectNodeById(node.children, targetId);
        if (found) return found;
      }
    }
    console.log('Узел с ID', targetId, 'не найден');
    return null;
  };

  // Функция для открытия дерева к узлу
  const openTreeToNode = async (searchResult) => {
    console.log('=== НАЧАЛО openTreeToNode ===');
    console.log('Найденный навык:', searchResult);
    
    // Очищаем состояние поиска
    setSearch("");
    setSearchResults([]);
    setSearchDropdown(false);
    setExpandedDescId(null);
    
    try {
      // Сначала попробуем найти узел в текущем дереве
      let foundNode = findAndSelectNodeById(tree, searchResult.id);
      console.log('Поиск узла в текущем дереве:', foundNode);
      
      if (foundNode) {
        console.log('Узел найден в дереве, выбираем его');
        handleSelectNode(foundNode);
        return;
      }
      
      // Если узел не найден, но есть родитель, попробуем загрузить родителя
      if (searchResult.parentId && !foundNode) {
        console.log('У навыка есть родитель с ID:', searchResult.parentId);
        
        // Ищем родительскую компетенцию в дереве
        const parentNode = findAndSelectNodeById(tree, searchResult.parentId);
        console.log('Найденная родительская компетенция:', parentNode);
        
        if (parentNode) {
          // Раскрываем родительскую компетенцию
          console.log('Раскрываем родительскую компетенцию');
          setOpenNodes(prev => ({ ...prev, [parentNode.id]: true }));
          
          // Если дочерние элементы не загружены, загружаем их
          if (!parentNode.childrenLoaded) {
            console.log('Загружаем дочерние элементы компетенции');
            await loadChildren(parentNode);
            
            // Ждем обновления дерева
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Повторно ищем узел после загрузки детей
            foundNode = findAndSelectNodeById(tree, searchResult.id);
            console.log('Поиск узла после загрузки детей:', foundNode);
          } else {
            // Если дети уже загружены, сразу ищем узел
            console.log('Дочерние элементы уже загружены, ищем узел');
            foundNode = findAndSelectNodeById(tree, searchResult.id);
            console.log('Поиск узла в загруженных детях:', foundNode);
          }
        } else {
          console.log('Родительская компетенция НЕ НАЙДЕНА в дереве!');
          // Если родитель не найден, возможно нужно загрузить корневые элементы
          console.log('Попробуем перезагрузить корневые элементы');
          setLoading(true);
          try {
            const data = await fetchRootSkills(apiBase);
            let treeData = [];
            if (Array.isArray(data) && data.length === 1 && data[0].level === 0) {
              treeData = data[0].skills;
            } else if (Array.isArray(data)) {
              treeData = data.flatMap(g => g.skills || []);
            }
            setTree(treeData);
            setLoading(false);
            
            // Теперь попробуем найти узел в обновленном дереве
            foundNode = findAndSelectNodeById(treeData, searchResult.id);
            console.log('Поиск узла в обновленном дереве:', foundNode);
          } catch (e) {
            console.error('Ошибка при перезагрузке дерева:', e);
            setLoading(false);
          }
        }
      }
      
      // Финальная попытка найти узел
      if (!foundNode) {
        console.log('Финальный поиск узла в дереве...');
        await new Promise(resolve => setTimeout(resolve, 300));
        foundNode = findAndSelectNodeById(tree, searchResult.id);
        console.log('Финальный результат поиска:', foundNode);
      }
      
      if (foundNode) {
        console.log('Выбираем найденный узел');
        handleSelectNode(foundNode);
        console.log('Узел выбран');
      } else {
        console.log('Узел НЕ НАЙДЕН в дереве, создаем временный объект');
        // Создаем временный объект для отображения
        const tempNode = {
          id: searchResult.id,
          title: searchResult.title,
          description: searchResult.description,
          parentId: searchResult.parentId,
          level: searchResult.level || 0,
          orderNum: searchResult.orderNum || 0,
          hasChildren: searchResult.hasChildren || false
        };
        handleSelectNode(tempNode);
        console.log('Временный узел создан и выбран');
      }
    } catch (e) {
      console.error('Ошибка при открытии дерева к узлу:', e);
      // В случае ошибки все равно показываем навык
      handleSelectNode({
        id: searchResult.id,
        title: searchResult.title,
        description: searchResult.description,
        parentId: searchResult.parentId,
        level: searchResult.level || 0,
        orderNum: searchResult.orderNum || 0,
        hasChildren: searchResult.hasChildren || false
      });
    }
    console.log('=== КОНЕЦ openTreeToNode ===');
  };

  // Функции для подсветки текста
  const highlight = (text, searchTerm) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark style="background: yellow; color: black;">$1</mark>');
  };

  const getSnippet = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const index = text.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (index === -1) return text.substring(0, 100) + '...';
    
    const start = Math.max(0, index - 50);
    const end = Math.min(text.length, index + searchTerm.length + 50);
    return (start > 0 ? '...' : '') + text.substring(start, end) + (end < text.length ? '...' : '');
  };

  const handleSkillUpdated = async (updatedSkill) => {
    setLoading(true);
    try {
      const data = await fetchRootSkills(apiBase);
      let treeData = [];
      if (Array.isArray(data) && data.length === 1 && data[0].level === 0) {
        treeData = data[0].skills;
      } else if (Array.isArray(data)) {
        treeData = data.flatMap(g => g.skills || []);
      }
      setTree(treeData);
      setSelectedNode(updatedSkill);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillDeleted = async (deletedId) => {
    setLoading(true);
    try {
      const data = await fetchRootSkills(apiBase);
      let treeData = [];
      if (Array.isArray(data) && data.length === 1 && data[0].level === 0) {
        treeData = data[0].skills;
      } else if (Array.isArray(data)) {
        treeData = data.flatMap(g => g.skills || []);
      }
      setTree(treeData);
      setSelectedNode(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App" style={{ background: darkPurple, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SearchHeader
        search={search}
        setSearch={setSearch}
        searchInputRef={searchInputRef}
        setSearchDropdown={setSearchDropdown}
        searchDropdown={searchDropdown}
      >
        <ContextSearchResults
          search={search}
          searchDropdown={searchDropdown}
          searchLoading={searchLoading}
          searchResults={searchResults}
          expandedDescId={expandedDescId}
          setExpandedDescId={setExpandedDescId}
          onResultClick={openTreeToNode}
          setSearchDropdown={setSearchDropdown}
          setSearch={setSearch}
          searchInputRef={searchInputRef}
          getSnippet={getSnippet}
          highlight={highlight}
          currentPage="skills"
        />
      </SearchHeader>
      <TopNav current="skills" />
      <div className="split-view">
        <div className="split-tree">
          <div style={{ color: accent, fontWeight: 700, fontSize: 24, textAlign: 'center', marginBottom: 12 }}>Категории навыков</div>
          {loading ? <div style={{ color: '#fff' }}>Загрузка...</div> : error ? <div style={{ color: 'red' }}>{error}</div> : (
            <TreeDirectory
              nodes={tree}
              openNodes={openNodes}
              setOpenNodes={setOpenNodes}
              loadChildren={loadChildren}
              onSelectNode={handleSelectNode}
              selectedNodeId={selectedNode?.id}
            />
          )}
        </div>
        <ContentPanel
          selectedNode={selectedNode}
          findAndSelectNodeById={findAndSelectNodeById}
          handleSelectNode={handleSelectNode}
          tree={tree}
          apiBase={apiBase}
          onSkillUpdated={handleSkillUpdated}
          onSkillAdded={()=>{}}
          onSkillDeleted={handleSkillDeleted}
          rootSkills={rootSkills}
        />
      </div>
    </div>
  );
}

